import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Checkbox,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  Select,
  Switch,
} from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowDown,
  faArrowUp,
  faTrash,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import type { TableModel } from "utils/tableModel"
import {
  DataTile,
  DifferenceMeasure,
  Measure,
  MeasureKind,
  NetMeasure,
  Page,
  RankMeasure,
  RowsDisplay,
  isDataTile,
  newId,
} from "features/Dashboard/types/dashboardDocument"
import {
  runtimeModel,
  useDashboardStore,
} from "features/Dashboard/runtime/dashboardStore"
import {
  defaultNpsBands,
  measureProblems,
  parseScaleValue,
} from "features/Dashboard/runtime/measures"
import { leafLabel } from "features/Dashboard/runtime/modelAdapters"
import {
  appendMeasures,
  appendNps,
  carryColour,
  inCategoryOrder,
  uniqueLabel,
} from "features/Dashboard/runtime/measureEdits"
import { TYPE_LABEL } from "features/Dashboard/components/Tiles/TileChrome.component"
import NetHint from "./NetHint.component"

export const TRANSPOSED_HINT =
  "Measures are not available on a transposed table. Untick Transpose in Display to use them."
const NPS_NEEDS =
  "NPS needs a 0 to 10 scale: seven or more categories with a number."

const KIND_LABEL: Record<MeasureKind, string> = {
  net: "Net",
  difference: "Difference",
  mean: "Mean",
  rolling: "Rolling average",
  delta: "Change",
  rank: "Rank",
}
const ROWS_OPTIONS: { value: RowsDisplay; label: string }[] = [
  { value: "categories", label: "Categories" },
  { value: "measures", label: "Measures" },
  { value: "both", label: "Both" },
]
const DIRECTION_OPTIONS: { value: RankMeasure["direction"]; label: string }[] =
  [
    { value: "desc", label: "Highest is 1st" },
    { value: "asc", label: "Lowest is 1st" },
  ]

type FormKind =
  | "nps"
  | "top"
  | "bottom"
  | "mean"
  | "net"
  | "rolling"
  | "delta"
  | "rank"

interface Category {
  id: string
  label: string
}
interface CopyTarget {
  pageId: string
  tileId: string
  label: string
}
type SourceOptions = {
  label: string
  options: { value: string; label: string }[]
}[]

/** The row categories of the last result, each once, Totals left out. */
const categoriesOf = (model: TableModel | undefined): Category[] => {
  if (!model) return []
  const seen = new Set<string>()
  return model.down.leaves.flatMap((l) => {
    if (l.isTotal || seen.has(l.id)) return []
    seen.add(l.id)
    return [{ id: l.id, label: leafLabel(l) }]
  })
}

/** How many non-Totals columns the last result has: its periods. */
const columnCount = (model: TableModel | undefined): number =>
  model
    ? new Set(model.across.leaves.filter((l) => !l.isTotal).map((l) => l.id))
        .size
    : 0

/** Whether `tile` has a rolling measure. */
const hasRolling = (tile: DataTile): boolean =>
  tile.measures.some((m) => m.kind === "rolling")

/** The rows and measures `m` reads by id. */
const readsOf = (m: Measure): string[] => {
  switch (m.kind) {
    case "difference":
      return [m.plus, m.minus]
    case "rolling":
    case "delta":
    case "rank":
      return [m.source]
    default:
      return []
  }
}

/** Measure `id` and the measures it reads, directly or not, in list order. */
const withDependencies = (measures: Measure[], id: string): Measure[] => {
  const byId = new Map(measures.map((m) => [m.id, m]))
  const keep = new Set<string>()
  const visit = (x: string): void => {
    const m = byId.get(x)
    if (!m || keep.has(x)) return
    keep.add(x)
    readsOf(m).forEach(visit)
  }
  visit(id)
  return measures.filter((m) => keep.has(m.id))
}

/** The measures that read `id`, directly or not, in list order. */
const dependantsOf = (measures: Measure[], id: string): Measure[] => {
  const gone = new Set([id])
  for (let grew = true; grew; ) {
    grew = false
    measures.forEach((m) => {
      if (!gone.has(m.id) && readsOf(m).some((x) => gone.has(x))) {
        gone.add(m.id)
        grew = true
      }
    })
  }
  return measures.filter((m) => m.id !== id && gone.has(m.id))
}

/**
 * `tile` without measure `id` and the measures that read it, and without
 * references to them: a KPI row or scorecard source naming one goes back
 * to its default, and they leave the hidden lists, the category order and
 * the category labels. A tile left without measures shows its categories.
 */
const withoutMeasure = (tile: DataTile, id: string): DataTile => {
  const ids = new Set([id, ...dependantsOf(tile.measures, id).map((m) => m.id)])
  const keep = (x: string): boolean => !ids.has(x)
  const measures = tile.measures.filter((m) => keep(m.id))
  const { order, hidden, labels } = tile.categories
  const categories = {
    ...tile.categories,
    ...(order ? { order: order.filter(keep) } : {}),
    hidden: hidden.filter(keep),
    labels: Object.fromEntries(
      Object.entries(labels).filter(([row]) => keep(row))
    ),
  }
  const display =
    measures.length === 0
      ? { ...tile.display, rows: "categories" as const }
      : tile.display
  switch (tile.type) {
    case "kpi": {
      const { row } = tile.visual
      return {
        ...tile,
        measures,
        categories,
        display,
        visual: { ...tile.visual, row: row !== null && keep(row) ? row : null },
      }
    }
    case "scorecard": {
      const { source } = tile.visual
      return {
        ...tile,
        measures,
        categories,
        display,
        visual: { ...tile.visual, source: keep(source) ? source : "" },
      }
    }
    case "line":
      return {
        ...tile,
        measures,
        categories,
        display,
        visual: {
          ...tile.visual,
          hiddenSeries: tile.visual.hiddenSeries.filter(keep),
        },
      }
    case "bar":
    case "pie":
    case "table":
      return { ...tile, measures, categories, display }
  }
}

/** Copies of `measures` under new ids, their references to each other following. */
const reidentified = (measures: Measure[]): Measure[] => {
  const ids = new Map(measures.map((m) => [m.id, newId("measure")]))
  const to = (x: string): string => ids.get(x) ?? x
  return measures.map((m): Measure => {
    const id = to(m.id)
    switch (m.kind) {
      case "difference":
        return { ...m, id, plus: to(m.plus), minus: to(m.minus) }
      case "rolling":
      case "delta":
      case "rank":
        return { ...m, id, source: to(m.source) }
      case "net":
        return { ...m, id, members: [...m.members] }
      case "mean":
        return { ...m, id, scaleValues: { ...m.scaleValues } }
    }
  })
}

/**
 * The other data tiles that can take `tile`'s measures: builder tiles with
 * the same first row variable, so the category ids match. A transposed
 * table is left out, as it shows no measures.
 */
const copyTargets = (pages: Page[], tile: DataTile): CopyTarget[] => {
  const row = tile.source.kind === "builder" ? tile.source.rows[0] : undefined
  if (!row) return []
  const named = pages.flatMap((p) =>
    p.tiles.flatMap((t) =>
      t.id !== tile.id &&
      isDataTile(t) &&
      t.source.kind === "builder" &&
      t.source.rows[0] === row &&
      !(t.type === "table" && t.visual.transposed)
        ? [
            {
              pageId: p.id,
              tileId: t.id,
              label: `${t.title || TYPE_LABEL[t.type]}${
                pages.length > 1 ? ` (${p.title})` : ""
              }`,
            },
          ]
        : []
    )
  )
  // Untitled tiles of one type share a name: number them.
  const total = new Map<string, number>()
  named.forEach((t) => total.set(t.label, (total.get(t.label) ?? 0) + 1))
  const seen = new Map<string, number>()
  return named.map((t) => {
    if ((total.get(t.label) ?? 0) < 2) return t
    const k = (seen.get(t.label) ?? 0) + 1
    seen.set(t.label, k)
    return { ...t, label: `${t.label} ${k}` }
  })
}

/** One line on what a measure is made of. */
const recipeOf = (m: Measure, nameOf: (id: string) => string): string => {
  switch (m.kind) {
    case "net":
      return m.members.map(nameOf).join(" + ")
    case "difference":
      return `${nameOf(m.plus)} minus ${nameOf(m.minus)}`
    case "mean": {
      const values = Object.values(m.scaleValues)
      if (values.length === 0) return "No scored categories"
      const low = Math.min(...values)
      const high = Math.max(...values)
      const n = values.length
      return `${n} ${n === 1 ? "category" : "categories"} scored ${
        low === high ? low : `${low} to ${high}`
      }`
    }
    case "rolling":
      return `${nameOf(m.source)} over ${m.periods} periods${
        m.requireFullWindow ? ", full windows only" : ""
      }`
    case "delta":
      return `${nameOf(m.source)} against the column before`
    case "rank":
      return `${nameOf(m.source)}, ${
        m.direction === "desc" ? "highest" : "lowest"
      } is 1st`
  }
}

/**
 * A form's name: the default until the author types one, and the default
 * again when the field is cleared.
 */
const useDraftName = (fallback: string) => {
  const [typed, setTyped] = useState<string | null>(null)
  return {
    value: typed ?? fallback,
    fallback,
    onChange: setTyped,
    name: typed?.trim() || fallback,
  }
}

const NameField: React.FC<{
  value: string
  fallback: string
  onChange: (v: string) => void
}> = ({ value, fallback, onChange }) => (
  <label className="dq-field">
    <span>Name</span>
    <Input
      size="small"
      value={value}
      placeholder={fallback}
      onChange={(e) => onChange(e.target.value)}
    />
  </label>
)

/** The inline card a preset opens: its fields, then Cancel and Add. */
const FormCard: React.FC<{
  title: string
  addLabel: string
  canAdd: boolean
  onAdd: () => void
  onCancel: () => void
  children: React.ReactNode
}> = ({ title, addLabel, canAdd, onAdd, onCancel, children }) => (
  <div className="dq-measure-form" role="group" aria-label={title}>
    {children}
    <div className="dq-measure-form__actions">
      <Button size="small" onClick={onCancel}>
        Cancel
      </Button>
      <Button size="small" type="primary" disabled={!canAdd} onClick={onAdd}>
        {addLabel}
      </Button>
    </div>
  </div>
)

interface FormProps {
  onAdd: (measures: Measure[]) => void
  onCancel: () => void
}

const NpsForm: React.FC<{
  categories: Category[]
  bands: { promoters: string[]; detractors: string[] }
  onAdd: (nets: [NetMeasure, NetMeasure], score: DifferenceMeasure) => void
  onCancel: () => void
}> = ({ categories, bands, onAdd, onCancel }) => {
  const name = useDraftName("NPS")
  const [promoters, setPromoters] = useState(bands.promoters)
  const [detractors, setDetractors] = useState(bands.detractors)
  // A category sits in one band at most.
  const options = (other: string[]) =>
    categories.map((c) => ({
      value: c.id,
      label: c.label,
      disabled: other.includes(c.id),
    }))
  const add = (): void => {
    const p: NetMeasure = {
      id: newId("measure"),
      label: "Promoters",
      kind: "net",
      members: inCategoryOrder(categories, promoters),
    }
    const d: NetMeasure = {
      id: newId("measure"),
      label: "Detractors",
      kind: "net",
      members: inCategoryOrder(categories, detractors),
    }
    const score: DifferenceMeasure = {
      id: newId("measure"),
      label: name.name,
      kind: "difference",
      plus: p.id,
      minus: d.id,
      format: "points",
    }
    onAdd([p, d], score)
  }
  return (
    <FormCard
      title="NPS"
      addLabel="Add NPS"
      canAdd={promoters.length > 0 && detractors.length > 0}
      onAdd={add}
      onCancel={onCancel}
    >
      <NameField {...name} />
      <label className="dq-field">
        <span>Promoters</span>
        <Select
          size="small"
          mode="multiple"
          value={promoters}
          options={options(detractors)}
          onChange={setPromoters}
        />
      </label>
      <label className="dq-field">
        <span>Detractors</span>
        <Select
          size="small"
          mode="multiple"
          value={detractors}
          options={options(promoters)}
          onChange={setDetractors}
        />
      </label>
      <p className="dq-field__hint">
        Adds the Promoters and Detractors nets and the score, Promoters minus
        Detractors in points.
      </p>
    </FormCard>
  )
}

const BoxForm: React.FC<
  FormProps & { side: "top" | "bottom"; categories: Category[] }
> = ({ side, categories, onAdd, onCancel }) => {
  const [k, setK] = useState(Math.min(2, categories.length))
  const word = side === "top" ? "Top" : "Bottom"
  const name = useDraftName(`${word} ${k} box`)
  const members =
    side === "top"
      ? categories.slice(Math.max(0, categories.length - k))
      : categories.slice(0, k)
  return (
    <FormCard
      title={`${word} box`}
      addLabel="Add net"
      canAdd={members.length > 0}
      onAdd={() =>
        onAdd([
          {
            id: newId("measure"),
            label: name.name,
            kind: "net",
            members: members.map((c) => c.id),
          },
        ])
      }
      onCancel={onCancel}
    >
      <NameField {...name} />
      <label className="dq-field">
        <span>Categories in the box</span>
        <InputNumber
          size="small"
          min={1}
          max={categories.length}
          precision={0}
          value={k}
          onChange={(v) =>
            setK(
              typeof v === "number"
                ? Math.min(categories.length, Math.max(1, v))
                : 1
            )
          }
        />
      </label>
      <div className="dq-field">
        <span>
          {side === "top" ? "The last" : "The first"} {k} in the list
        </span>
        <ul
          className="dq-measure-form__preview"
          aria-label="Categories in the net"
        >
          {members.map((c) => (
            <li key={c.id}>{c.label}</li>
          ))}
        </ul>
      </div>
      <NetHint />
    </FormCard>
  )
}

const MeanForm: React.FC<FormProps & { categories: Category[] }> = ({
  categories,
  onAdd,
  onCancel,
}) => {
  const name = useDraftName("Mean")
  const [values, setValues] = useState<Record<string, number | null>>(() =>
    Object.fromEntries(categories.map((c) => [c.id, parseScaleValue(c.label)]))
  )
  const scored = categories.flatMap((c) => {
    const v = values[c.id]
    return typeof v === "number" ? [[c.id, v] as const] : []
  })
  return (
    <FormCard
      title="Mean"
      addLabel="Add mean"
      canAdd={scored.length > 0}
      onAdd={() =>
        onAdd([
          {
            id: newId("measure"),
            label: name.name,
            kind: "mean",
            scaleValues: Object.fromEntries(scored),
          },
        ])
      }
      onCancel={onCancel}
    >
      <NameField {...name} />
      <div className="dq-field">
        <span>Score per category</span>
        <div className="dq-measure-scale">
          {categories.map((c) => (
            <div key={c.id} className="dq-measure-scale__row">
              <span className="dq-measure-scale__label" title={c.label}>
                {c.label}
              </span>
              <InputNumber
                size="small"
                aria-label={`Score for ${c.label}`}
                placeholder="None"
                value={values[c.id]}
                onChange={(v) =>
                  setValues((prev) => ({
                    ...prev,
                    [c.id]: typeof v === "number" ? v : null,
                  }))
                }
              />
            </div>
          ))}
        </div>
        <span className="dq-field__hint">
          A category without a score, such as Don&apos;t know, is left out of
          the mean and its base.
        </span>
      </div>
    </FormCard>
  )
}

const NetForm: React.FC<FormProps & { categories: Category[] }> = ({
  categories,
  onAdd,
  onCancel,
}) => {
  const name = useDraftName("Net")
  const [ticked, setTicked] = useState<string[]>([])
  const members = inCategoryOrder(categories, ticked)
  return (
    <FormCard
      title="Custom net"
      addLabel="Add net"
      canAdd={members.length > 0}
      onAdd={() =>
        onAdd([
          { id: newId("measure"), label: name.name, kind: "net", members },
        ])
      }
      onCancel={onCancel}
    >
      <NameField {...name} />
      <div className="dq-field">
        <span>Categories in the net</span>
        <Checkbox.Group
          className="dq-measure-form__ticks"
          value={ticked}
          options={categories.map((c) => ({ value: c.id, label: c.label }))}
          onChange={(v) => setTicked(v as string[])}
        />
      </div>
      <NetHint />
    </FormCard>
  )
}

const SourceField: React.FC<{
  value: string | undefined
  options: SourceOptions
  onChange: (v: string) => void
}> = ({ value, options, onChange }) => (
  <label className="dq-field">
    <span>Source</span>
    <Select
      size="small"
      value={value}
      options={options}
      placeholder="A category or measure"
      onChange={onChange}
    />
  </label>
)

type SourceFormProps = FormProps & {
  sources: SourceOptions
  nameOf: (id: string) => string
}

const RollingForm: React.FC<SourceFormProps> = ({
  sources,
  nameOf,
  onAdd,
  onCancel,
}) => {
  const [source, setSource] = useState<string>()
  const [periods, setPeriods] = useState(3)
  const [full, setFull] = useState(true)
  const name = useDraftName(
    source ? `${nameOf(source)}, ${periods}-period average` : "Rolling average"
  )
  return (
    <FormCard
      title="Rolling average"
      addLabel="Add rolling average"
      canAdd={source !== undefined}
      onAdd={() => {
        if (!source) return
        onAdd([
          {
            id: newId("measure"),
            label: name.name,
            kind: "rolling",
            source,
            periods,
            requireFullWindow: full,
          },
        ])
      }}
      onCancel={onCancel}
    >
      <NameField {...name} />
      <SourceField value={source} options={sources} onChange={setSource} />
      <label className="dq-field">
        <span>Periods</span>
        <InputNumber
          size="small"
          min={2}
          max={12}
          precision={0}
          value={periods}
          onChange={(v) =>
            setPeriods(
              typeof v === "number" ? Math.min(12, Math.max(2, v)) : 2
            )
          }
        />
      </label>
      <div className="dq-toggle">
        <label className="dq-field dq-field--row">
          <span>Full windows only</span>
          <Switch size="small" checked={full} onChange={setFull} />
        </label>
        <p className="dq-field__hint">
          {full
            ? `The first ${periods - 1} ${
                periods === 2 ? "column stays" : "columns stay"
              } blank rather than average fewer periods.`
            : "The first columns average the periods there are so far."}
        </p>
      </div>
    </FormCard>
  )
}

const DeltaForm: React.FC<SourceFormProps> = ({
  sources,
  nameOf,
  onAdd,
  onCancel,
}) => {
  const [source, setSource] = useState<string>()
  const name = useDraftName(
    source ? `${nameOf(source)} change` : "Change vs previous"
  )
  return (
    <FormCard
      title="Change vs previous"
      addLabel="Add change"
      canAdd={source !== undefined}
      onAdd={() => {
        if (!source) return
        onAdd([
          { id: newId("measure"), label: name.name, kind: "delta", source },
        ])
      }}
      onCancel={onCancel}
    >
      <NameField {...name} />
      <SourceField value={source} options={sources} onChange={setSource} />
      <p className="dq-field__hint">
        Each column minus the column before it. Percentages change in points.
      </p>
    </FormCard>
  )
}

const RankForm: React.FC<SourceFormProps> = ({
  sources,
  nameOf,
  onAdd,
  onCancel,
}) => {
  const [source, setSource] = useState<string>()
  const [direction, setDirection] =
    useState<RankMeasure["direction"]>("desc")
  const name = useDraftName(source ? `${nameOf(source)} rank` : "Rank")
  return (
    <FormCard
      title="Rank"
      addLabel="Add rank"
      canAdd={source !== undefined}
      onAdd={() => {
        if (!source) return
        onAdd([
          {
            id: newId("measure"),
            label: name.name,
            kind: "rank",
            source,
            direction,
          },
        ])
      }}
      onCancel={onCancel}
    >
      <NameField {...name} />
      <SourceField value={source} options={sources} onChange={setSource} />
      <label className="dq-field">
        <span>Order</span>
        <Select
          size="small"
          value={direction}
          options={DIRECTION_OPTIONS}
          onChange={setDirection}
        />
      </label>
    </FormCard>
  )
}

/** A measure's label, written when the field loses focus or on Enter. */
const MeasureLabel: React.FC<{
  label: string
  onCommit: (label: string) => void
}> = ({ label, onCommit }) => {
  const [draft, setDraft] = useState(label)
  useEffect(() => setDraft(label), [label])
  const commit = (): void => {
    const text = draft.trim()
    if (!text) setDraft(label)
    else if (text !== label) onCommit(text)
  }
  return (
    <Input
      size="small"
      className="dq-measure__label"
      aria-label={`Rename ${label}`}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onPressEnter={commit}
    />
  )
}

/**
 * The tile's measures: presets that add nets, NPS and means over the
 * categories of the last result, calculations along the columns (rolling
 * average, change versus the column before, rank), the list of measures
 * with their problems, and which rows the tile shows. Writes go through
 * `updateTileView`: measures are computed from the last result, so editing
 * them does not run the tile again. The one exception is an edit that adds
 * a tile's first rolling measure or removes its last, which changes what
 * the tile runs and so goes through `updateTile`. A transposed table shows
 * no measures, so the tab only says so.
 */
const MeasuresTab: React.FC<{ tile: DataTile; pageId: string }> = ({
  tile,
  pageId,
}) => {
  const updateTile = useDashboardStore((s) => s.updateTile)
  const updateTileView = useDashboardStore((s) => s.updateTileView)
  // A repeated tile lists from its first panel: every panel has the same
  // rows and columns.
  const model = useDashboardStore((s) => runtimeModel(s.tiles[tile.id]))
  const pages = useDashboardStore((s) => s.document.pages)
  const theme = useDashboardStore((s) => s.document.theme)
  const setTheme = useDashboardStore((s) => s.setTheme)
  /**
   * Writes `fn` to `target`: view only, unless it changes whether the tile
   * has a rolling measure, which changes what the tile runs (its run keeps
   * "n" for the rolling average's weights; see `planTileRun`).
   */
  const writeMeasures = (
    targetPage: string,
    target: DataTile,
    fn: (t: DataTile) => DataTile
  ): void => {
    const rerun = hasRolling(fn(target)) !== hasRolling(target)
    const write = rerun ? updateTile : updateTileView
    write(targetPage, target.id, (t) => (isDataTile(t) ? fn(t) : t))
  }
  const [open, setOpen] = useState<FormKind | null>(null)
  const [copied, setCopied] = useState<string | null>(null)
  // Remounts the copy pickers after a copy so they show their placeholder.
  const [copyRound, setCopyRound] = useState(0)
  const { measures } = tile

  const categories = useMemo(() => categoriesOf(model), [model])
  const bands = useMemo(
    () => (model ? defaultNpsBands(model.down.leaves) : null),
    [model]
  )
  const columns = useMemo(() => columnCount(model), [model])
  const problems = useMemo(
    () => (model ? measureProblems(model, measures) : []),
    [model, measures]
  )
  const targets = useMemo(() => copyTargets(pages, tile), [pages, tile])
  const nameOf = useMemo(() => {
    const names = new Map<string, string>([
      ...categories.map((c) => [c.id, c.label] as const),
      ...measures.map((m) => [m.id, m.label] as const),
    ])
    return (id: string): string => names.get(id) ?? id
  }, [categories, measures])
  const sources = useMemo<SourceOptions>(
    () => [
      ...(categories.length > 0
        ? [
            {
              label: "Categories",
              options: categories.map((c) => ({ value: c.id, label: c.label })),
            },
          ]
        : []),
      ...(measures.length > 0
        ? [
            {
              label: "Measures",
              options: measures.map((m) => ({ value: m.id, label: m.label })),
            },
          ]
        : []),
    ],
    [categories, measures]
  )

  if (tile.type === "table" && tile.visual.transposed) {
    return (
      <div className="dq-inspector__section">
        <p className="dq-field__hint">{TRANSPOSED_HINT}</p>
      </div>
    )
  }

  const set = (fn: (t: DataTile) => DataTile): void =>
    writeMeasures(pageId, tile, fn)
  const add = (added: Measure[]): void => {
    set((t) => appendMeasures(t, added))
    setOpen(null)
  }
  const addNps = (nets: [NetMeasure, NetMeasure], score: Measure): void => {
    set((t) => appendNps(t, nets, score))
    setOpen(null)
  }
  const setMeasures = (fn: (ms: Measure[]) => Measure[]): void =>
    set((t) => ({ ...t, measures: fn(t.measures) }))
  const remove = (id: string): void => set((t) => withoutMeasure(t, id))
  const move = (id: string, by: -1 | 1): void =>
    setMeasures((ms) => {
      const i = ms.findIndex((m) => m.id === id)
      const j = i + by
      if (i < 0 || j < 0 || j >= ms.length) return ms
      const next = [...ms]
      ;[next[i], next[j]] = [next[j], next[i]]
      return next
    })
  // A measure's dashboard colour goes with it to its new name (copied).
  const rename = (id: string, label: string): void => {
    const before = measures.find((m) => m.id === id)?.label
    const taken = new Set(
      measures.filter((m) => m.id !== id).map((m) => m.label)
    )
    const next = uniqueLabel(label, taken)
    setMeasures((ms) => ms.map((m) => (m.id === id ? { ...m, label: next } : m)))
    const colours =
      before === undefined
        ? null
        : carryColour(theme.categoryColours, before, next)
    if (colours) setTheme({ ...theme, categoryColours: colours })
  }
  const copy = (m: Measure, target: CopyTarget): void => {
    const copies = reidentified(withDependencies(measures, m.id))
    const into = pages
      .find((p) => p.id === target.pageId)
      ?.tiles.find((t) => t.id === target.tileId)
    if (into && isDataTile(into)) {
      writeMeasures(target.pageId, into, (t) => appendMeasures(t, copies))
    }
    const extra = copies.length - 1
    setCopied(
      `Copied ${m.label}${
        extra > 0
          ? ` and the ${extra === 1 ? "measure" : `${extra} measures`} it reads`
          : ""
      } to ${target.label}.`
    )
    setCopyRound((r) => r + 1)
  }

  const close = (): void => setOpen(null)
  const opener = (
    kind: FormKind,
    label: string,
    disabled: boolean,
    why?: string
  ) => (
    <button
      type="button"
      className="dq-btn"
      aria-expanded={open === kind}
      disabled={disabled}
      title={disabled ? why : undefined}
      onClick={() => setOpen((o) => (o === kind ? null : kind))}
    >
      {label}
    </button>
  )
  const noCategories = categories.length === 0
  const noPeriods = columns < 2
  const row = tile.source.kind === "builder" ? tile.source.rows[0] : undefined
  const copyHint =
    tile.source.kind === "script"
      ? "Copy to tile needs a builder tile: a script tile has no row variable to match."
      : targets.length === 0 && row
        ? `Copy to tile lists the tiles whose first row variable is ${row}. There are none yet.`
        : null
  const sourceForm = { sources, nameOf, onAdd: add, onCancel: close }

  return (
    <div className="dq-inspector__section dq-measures">
      <h4 className="dq-inspector__h">Add a measure</h4>
      {noCategories && (
        <p className="dq-field__hint">
          {model
            ? "The last result has no categories to combine."
            : "Run the tile to list its categories."}
        </p>
      )}
      <div className="dq-measures__adds">
        {opener("nps", "NPS", !bands, NPS_NEEDS)}
        {opener("top", "Top 2 box", noCategories)}
        {opener("bottom", "Bottom 2 box", noCategories)}
        {opener("mean", "Mean", noCategories)}
        {opener("net", "Custom net", noCategories)}
      </div>
      {open === "nps" && bands && (
        <NpsForm
          categories={categories}
          bands={bands}
          onAdd={addNps}
          onCancel={close}
        />
      )}
      {(open === "top" || open === "bottom") && !noCategories && (
        <BoxForm
          key={open}
          side={open}
          categories={categories}
          onAdd={add}
          onCancel={close}
        />
      )}
      {open === "mean" && !noCategories && (
        <MeanForm categories={categories} onAdd={add} onCancel={close} />
      )}
      {open === "net" && !noCategories && (
        <NetForm categories={categories} onAdd={add} onCancel={close} />
      )}

      <h4 className="dq-inspector__h">Periods</h4>
      <p className="dq-field__hint">
        {!model
          ? "Run the tile to read its columns."
          : noPeriods
            ? "Needs a column variable with two or more categories, such as waves. Add one in Data."
            : "Reads the columns as periods, left to right: use it when they are waves or dates."}
      </p>
      <div className="dq-measures__adds">
        {opener("rolling", "Rolling average", noPeriods)}
        {opener("delta", "Change vs previous", noPeriods)}
      </div>
      {open === "rolling" && !noPeriods && <RollingForm {...sourceForm} />}
      {open === "delta" && !noPeriods && <DeltaForm {...sourceForm} />}

      <h4 className="dq-inspector__h">Rank</h4>
      <p className="dq-field__hint">
        {!model
          ? "Run the tile to read its columns."
          : noPeriods
            ? "Needs a column variable with two or more categories to rank."
            : "Ranks the columns by a category or measure."}
      </p>
      <div className="dq-measures__adds">
        {opener("rank", "Rank columns", noPeriods)}
      </div>
      {open === "rank" && !noPeriods && <RankForm {...sourceForm} />}

      <h4 className="dq-inspector__h">Measures</h4>
      {measures.length === 0 ? (
        <p className="dq-field__hint">No measures yet. Add one above.</p>
      ) : (
        <ol className="dq-measures__list" aria-label="Measures">
          {measures.map((m, i) => {
            const issues = problems.filter((p) => p.measureId === m.id)
            const recipe = recipeOf(m, nameOf)
            const dependants = dependantsOf(measures, m.id)
            const deleteButton = (
              <Button
                size="small"
                type="text"
                danger
                aria-label={`Delete ${m.label}`}
                icon={<FontAwesomeIcon icon={faTrash} />}
                onClick={
                  dependants.length > 0 ? undefined : () => remove(m.id)
                }
              />
            )
            return (
              <li
                key={m.id}
                className="dq-measure"
                data-problem={issues.length > 0 || undefined}
              >
                <div className="dq-measure__head">
                  <MeasureLabel
                    label={m.label}
                    onCommit={(label) => rename(m.id, label)}
                  />
                  <span className="dq-measure__kind">{KIND_LABEL[m.kind]}</span>
                  <Button
                    size="small"
                    type="text"
                    aria-label={`Move ${m.label} up`}
                    disabled={i === 0}
                    icon={<FontAwesomeIcon icon={faArrowUp} />}
                    onClick={() => move(m.id, -1)}
                  />
                  <Button
                    size="small"
                    type="text"
                    aria-label={`Move ${m.label} down`}
                    disabled={i === measures.length - 1}
                    icon={<FontAwesomeIcon icon={faArrowDown} />}
                    onClick={() => move(m.id, 1)}
                  />
                  {dependants.length > 0 ? (
                    <Popconfirm
                      title={`Delete ${m.label}?`}
                      description={`Also removes: ${dependants
                        .map((d) => d.label)
                        .join(", ")}`}
                      okText="Delete"
                      okButtonProps={{ danger: true }}
                      cancelText="Cancel"
                      onConfirm={() => remove(m.id)}
                    >
                      {deleteButton}
                    </Popconfirm>
                  ) : (
                    deleteButton
                  )}
                </div>
                <p className="dq-measure__recipe" title={recipe}>
                  {recipe}
                </p>
                {issues.map((p) => (
                  <p key={p.message} className="dq-measure__problem">
                    <FontAwesomeIcon icon={faTriangleExclamation} />
                    <span>{p.message}</span>
                  </p>
                ))}
                <Select
                  key={copyRound}
                  size="small"
                  className="dq-measure__copy"
                  aria-label={`Copy ${m.label} to tile`}
                  placeholder="Copy to tile"
                  disabled={targets.length === 0}
                  options={targets.map((t) => ({
                    value: t.tileId,
                    label: t.label,
                  }))}
                  onChange={(id: string) => {
                    const target = targets.find((t) => t.tileId === id)
                    if (target) copy(m, target)
                  }}
                />
              </li>
            )
          })}
        </ol>
      )}
      {measures.length > 0 && copyHint && (
        <p className="dq-field__hint">{copyHint}</p>
      )}
      {copied && (
        <p className="dq-field__hint dq-measures__status" role="status">
          {copied}
        </p>
      )}

      <h4 className="dq-inspector__h">Show</h4>
      <Radio.Group
        size="small"
        optionType="button"
        buttonStyle="solid"
        className="dq-measures__show"
        value={tile.display.rows}
        options={ROWS_OPTIONS}
        disabled={measures.length === 0}
        onChange={(e) => {
          const rows = e.target.value as RowsDisplay
          set((t) => ({ ...t, display: { ...t.display, rows } }))
        }}
      />
      {measures.length === 0 && (
        <p className="dq-field__hint">
          The tile shows its categories until it has a measure.
        </p>
      )}
    </div>
  )
}

export default MeasuresTab
